import React from "react";

// Smoothly animates an eggTransform bag (tx, ty, scale, scaleX,
// rotation) toward a target whenever the target changes. Active in
// any mode where the marc changes via state (landing/speedrun/creative)
// — author/label set `active=false` so slider/drag updates aren't
// fought by the tween.
//
// Why JS rAF and not a CSS transition: the egg is rendered to a
// canvas with the transform baked in (drawEggTransformed +
// applyLighting both consume eggTransform). Animating via CSS would
// require reworking the rendering pipeline so the egg renders at
// identity and the wrapper handles position; that's invasive and
// changes lighting semantics in subtle ways. Tweening the bag in
// React state is one extra rAF loop; the per-frame canvas re-light
// is fast enough at this canvas size for a one-second slide.
//
// Lighting is NOT tweened — eff.eggLights snaps at the moment the
// marcId changes (start of the move). This is intentional: trying to
// interpolate lights between marcs with potentially different array
// lengths gets messy fast, and the snap happens while drawing+photo
// are invisible (or the modal is dismissing, in creative mode) and
// the egg is in motion, so it reads as part of the transition rather
// than as a flash.
//
// Returns { current, tweening, tweeningRef }:
//   current     — the interpolated transform (or the target itself when
//                 inactive — no rAF, no animation, just passthrough).
//   tweening    — React state, true while a tween is in progress.
//                 Speedrun gates player input on this so the round
//                 doesn't start mid-flight. Useful as a render dep
//                 since it triggers re-renders on transitions.
//   tweeningRef — the SAME tweening info but mutated synchronously
//                 inside the hook's effect, so consumers reading it
//                 from another effect in the same commit cycle see
//                 the up-to-date value (whereas `tweening` from React
//                 state is still the prior render's). Speedrun's
//                 markStarted gate reads this — without it, the
//                 markStarted effect would race the tween's
//                 setTweening(true) and fire markStarted ~16ms
//                 before the slide actually begins, then the timer
//                 would count through the entire tween.
function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function lerpTransform(a, b, t) {
  return {
    tx: a.tx + (b.tx - a.tx) * t,
    ty: a.ty + (b.ty - a.ty) * t,
    scale: a.scale + (b.scale - a.scale) * t,
    scaleX: a.scaleX + (b.scaleX - a.scaleX) * t,
    rotation: a.rotation + (b.rotation - a.rotation) * t,
  };
}

function transformsEqual(a, b) {
  return (
    a.tx === b.tx &&
    a.ty === b.ty &&
    a.scale === b.scale &&
    a.scaleX === b.scaleX &&
    a.rotation === b.rotation
  );
}

export default function useTweenedEggTransform({
  target,
  durationMs,
  active,
}) {
  const [current, setCurrent] = React.useState(target);
  const [tweening, setTweening] = React.useState(false);
  // currentRef mirrors `current` synchronously so the next tween's
  // effect can read the latest value as its `from` without waiting
  // for a render cycle.
  const currentRef = React.useRef(target);
  // tweeningRef mirrors `tweening` synchronously — same purpose, but
  // for consumers that need the up-to-date value within the SAME
  // commit cycle that the tween starts/ends. See header comment for
  // the markStarted ordering issue this avoids.
  const tweeningRef = React.useRef(false);

  React.useEffect(() => {
    if (!active) {
      // Snap to target on deactivate — no animation, no rAF loop.
      currentRef.current = target;
      tweeningRef.current = false;
      setCurrent(target);
      setTweening(false);
      return;
    }
    if (transformsEqual(currentRef.current, target)) return;

    const from = currentRef.current;
    const start = performance.now();
    tweeningRef.current = true;
    setTweening(true);
    let raf;
    const tick = (now) => {
      const elapsed = now - start;
      const t = Math.min(1, elapsed / durationMs);
      const eased = easeInOutCubic(t);
      const next = lerpTransform(from, target, eased);
      currentRef.current = next;
      setCurrent(next);
      if (t < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        tweeningRef.current = false;
        setTweening(false);
      }
    };
    raf = requestAnimationFrame(tick);
    // Only cancel the rAF on cleanup; don't clear `tweening` here.
    // If the effect re-fires because `target` changed mid-tween, the
    // next run will start a new tween and re-set tweening=true. If it
    // re-fires because `active` flipped false, the inactive branch
    // above clears tweening. In neither case do we want a flicker
    // false→true between frames.
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs, active]);

  return {
    current: active ? current : target,
    tweening: active && tweening,
    tweeningRef,
  };
}
